import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { DEFAULT_CONTENT } from "@/lib/siteContent";

const inputCls =
  "w-full bg-white border border-[#263562]/15 rounded-lg px-4 py-2.5 text-sm text-[#1E2A4A] placeholder:text-[#949CA1] outline-none focus:border-[#3AB0E1] focus:ring-1 focus:ring-[#3AB0E1]/30 transition-all";
const labelCls =
  "block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-1.5";

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-[#263562]/10 p-7 shadow-sm mb-6">
    <h3 className="font-display font-semibold text-lg text-[#263562] mb-5">{title}</h3>
    {children}
  </div>
);

export default function ContentTab() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent({ ...DEFAULT_CONTENT, ...(r.data.key ? r.data : {}) }))
      .catch((e) => {
        toast.error(formatApiError(e));
        setContent(DEFAULT_CONTENT);
      });
  }, []);

  if (!content) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#3AB0E1]" />
      </div>
    );
  }

  const setLine = (i, k, v) => {
    const heroLines = [...content.heroLines];
    heroLines[i] = { ...heroLines[i], [k]: v };
    setContent({ ...content, heroLines });
  };

  const setStat = (i, k, v) => {
    const stats = [...content.stats];
    stats[i] = { ...stats[i], [k]: k === "value" || k === "decimals" ? Number(v) : v };
    setContent({ ...content, stats });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...content };
      delete payload.key;
      await api.put("/admin/content", payload);
      toast.success("Site content saved", { description: "Changes are live on the website now." });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-editor">
      <Section title="Hero Headline">
        <p className="text-xs text-[#949CA1] mb-4">Each line reveals one by one on page load. Accent lines render in sky blue.</p>
        <div className="space-y-3">
          {content.heroLines.map((l, i) => (
            <div key={i} className="flex items-center gap-3">
              <input
                data-testid={`content-hero-line-${i}`}
                value={l.text}
                onChange={(e) => setLine(i, "text", e.target.value)}
                className={inputCls}
              />
              <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                <input
                  data-testid={`content-hero-accent-${i}`}
                  type="checkbox"
                  checked={!!l.accent}
                  onChange={(e) => setLine(i, "accent", e.target.checked)}
                  className="w-4 h-4 accent-[#3AB0E1]"
                />
                <span className="text-xs text-[#5A6B85]">Accent</span>
              </label>
              <button
                onClick={() => setContent({ ...content, heroLines: content.heroLines.filter((_, j) => j !== i) })}
                className="shrink-0 p-2 text-[#949CA1] hover:text-red-500 transition-colors"
                aria-label="Remove line"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          data-testid="content-hero-add-line"
          onClick={() => setContent({ ...content, heroLines: [...content.heroLines, { text: "", accent: false }] })}
          className="mt-4 flex items-center gap-1.5 text-xs font-medium text-[#263562] border border-[#263562]/15 hover:border-[#3AB0E1] rounded-full px-4 py-2 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add line
        </button>
        <label className={`${labelCls} mt-6`}>Hero Sub-paragraph</label>
        <textarea
          data-testid="content-hero-sub"
          rows={3}
          value={content.heroSub}
          onChange={(e) => setContent({ ...content, heroSub: e.target.value })}
          className={`${inputCls} resize-y`}
        />
        <label className={`${labelCls} mt-6`}>Stats Ticker (one per line)</label>
        <textarea
          data-testid="content-ticker"
          rows={4}
          value={content.ticker.join("\n")}
          onChange={(e) => setContent({ ...content, ticker: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
          className={`${inputCls} resize-y`}
        />
      </Section>

      <Section title="Stats Counters">
        <div className="space-y-3">
          {content.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-[100px_90px_1fr_90px] gap-3 items-center">
              <input data-testid={`content-stat-value-${i}`} type="number" step="any" value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} className={inputCls} placeholder="Value" />
              <input value={s.suffix} onChange={(e) => setStat(i, "suffix", e.target.value)} className={inputCls} placeholder="Suffix" />
              <input data-testid={`content-stat-label-${i}`} value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} className={inputCls} placeholder="Label" />
              <input type="number" value={s.decimals} onChange={(e) => setStat(i, "decimals", e.target.value)} className={inputCls} placeholder="Decimals" />
            </div>
          ))}
        </div>
        <p className="text-xs text-[#949CA1] mt-3">Value · Suffix · Label · Decimal places</p>
      </Section>

      <Section title="Contact Details">
        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="content-contact-email" type="email" value={content.contactEmail} onChange={(e) => setContent({ ...content, contactEmail: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone / WhatsApp</label>
            <input data-testid="content-contact-phone" value={content.contactPhone} onChange={(e) => setContent({ ...content, contactPhone: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Headquarters</label>
            <input data-testid="content-contact-hq" value={content.contactHq} onChange={(e) => setContent({ ...content, contactHq: e.target.value })} className={inputCls} />
          </div>
        </div>
      </Section>

      <button
        data-testid="content-save-button"
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-full transition-colors duration-300"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save All Changes
      </button>
    </div>
  );
}
